import { AlertTriangle, Calendar, Camera, CheckCircle, Clock, ExternalLink, MapPin, MessageSquare, Share2, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhotoGallery } from '@/components/PhotoGallery';
import { formatPhone, mapsUrl } from '@/lib/formatters';

function useCustomerTabScope(data, helpers, actions) {
  return {
    ...data,
    ...helpers,
    ...actions,
  };
}


export function CustomerTimelineTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  return (
    <>
        <TabsContent value="timeline">
          {timeline.length === 0 ? (
            <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No lifecycle events yet</p></CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="customer-lifecycle-timeline">
              {timeline.map((item, i) => {
                const Icon = timelineIcons[item.type] || Clock;
                return (
                  <div key={`${item.type}-${item.entity_id}-${item.created_at}-${i}`} onClick={() => item.entity_id && openRecord(item.entity_type || item.type, { id: item.entity_id })} className="flex cursor-pointer gap-3 rounded-md border border-zinc-200 bg-white p-3 hover:bg-zinc-50" data-testid={`customer-timeline-item-${i}`}>
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-700">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-sm text-zinc-900" data-testid={`timeline-title-${i}`}>{item.title}</p>
                        {item.status && <Badge variant="secondary" className="text-[10px]">{item.status.replace('_', ' ')}</Badge>}
                        {item.amount > 0 && <span className="text-xs font-semibold text-zinc-700">{money(item.amount)}</span>}
                      </div>
                      {item.description && <p className="mt-1 text-sm text-zinc-600">{item.description}</p>}
                      <p className="mt-1 text-xs text-zinc-400">{item.created_at ? new Date(item.created_at).toLocaleString() : 'No timestamp'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
    </>
  );
}

export function CustomerLeadsTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  return (
    <>
        <TabsContent value="leads">
          {leads.length === 0 ? (
            <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No leads for this customer</p></CardContent></Card>
          ) : (
            <div className="space-y-2" data-testid="customer-leads-list">
              {leads.map(lead => (
                <div key={lead.id} onClick={() => openRecord('lead', lead)} className="flex cursor-pointer items-center justify-between p-3 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors" data-testid={`customer-open-lead-${lead.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-1 h-10 rounded-full" style={{ backgroundColor: getDBAColor(lead.dba_id) }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{lead.contact_name}</p>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        {lead.service_category && <span>{lead.service_category}</span>}
                        <span>{getDBAName(lead.dba_id)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {lead.estimated_value > 0 && <span className="text-sm font-semibold">${lead.estimated_value.toLocaleString()}</span>}
                    <Badge variant="secondary" className={`text-xs ${stageColors[lead.stage] || ''}`}>{lead.stage}</Badge>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" data-testid={`customer-open-lead-button-${lead.id}`}>Open</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
    </>
  );
}

export function CustomerEstimatesQuotesTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  const quoteById = new Map(quotes.map((quote) => [quote.id, quote]));
  const estimateById = new Map(estimates.map((estimate) => [estimate.id, estimate]));
  return (
    <>
        <TabsContent value="estimates-quotes">
          <Tabs defaultValue="estimates" className="w-full" data-testid="customer-estimates-quotes-subtabs">
            <TabsList className="mb-4 flex h-auto flex-wrap justify-start rounded-md border border-zinc-200 bg-white p-1">
              <TabsTrigger value="estimates" data-testid="customer-subtab-estimates">Estimates ({estimates.length})</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="customer-subtab-quotes">Quotes ({quotes.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="estimates">
              {estimates.length === 0 ? (
                <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No estimates for this customer</p></CardContent></Card>
              ) : (
                <div className="space-y-2" data-testid="customer-estimates-list">
                  {estimates.map(estimate => {
                    const linkedQuote = estimate.quote_id ? quoteById.get(estimate.quote_id) : null;
                    const hasMissingLinkedQuote = Boolean(estimate.quote_id && !linkedQuote);
                    return (
                    <div key={estimate.id} onClick={() => openRecord('estimate', estimate)} className="cursor-pointer rounded-sm border border-zinc-200 bg-white p-3 hover:bg-zinc-50" data-testid={`customer-estimate-${estimate.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-zinc-900" data-testid={`customer-estimate-number-${estimate.id}`}>{estimate.estimate_number}</p>
                          <p className="text-xs text-zinc-500 capitalize" data-testid={`customer-estimate-summary-${estimate.id}`}>{estimate.mode || 'quick'} · {estimate.calculation?.customer_range || ''} · {estimate.calculation?.load_category_name || ''}</p>
                          {estimate.quote_id && <p className={`mt-1 text-xs font-semibold ${linkedQuote ? 'text-blue-700' : 'text-amber-800'}`} data-testid={`customer-estimate-linked-quote-${estimate.id}`}>{linkedQuote ? `Linked quote: ${linkedQuote.quote_number}` : 'Linked quote unavailable'}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold" data-testid={`customer-estimate-total-${estimate.id}`}>{money(estimate.calculation?.final_price)}</span>
                          <Badge variant="secondary" className="text-xs" data-testid={`customer-estimate-status-${estimate.id}`}>{estimate.status}</Badge>
                          {estimate.quote_id && (hasMissingLinkedQuote ? <Badge variant="secondary" className="border border-amber-200 bg-amber-50 text-amber-800" data-testid={`customer-missing-linked-quote-${estimate.id}`}>Quote Missing</Badge> : <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={(event) => { event.stopPropagation(); openRecord('quote', linkedQuote); }} data-testid={`customer-open-linked-quote-${estimate.id}`}><ExternalLink className="mr-1 h-3 w-3" /> Quote</Button>)}
                          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" data-testid={`customer-open-estimate-button-${estimate.id}`}>Open</Button>
                        </div>
                      </div>
                      {estimate.actuals && <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid={`customer-estimate-actuals-${estimate.id}`}>Actual profit {money(estimate.actuals.actual_gross_profit)} · {estimate.actuals.actual_profit_margin}% margin</p>}
                    </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            <TabsContent value="quotes">
              {quotes.length === 0 ? (
                <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No quotes for this customer</p></CardContent></Card>
              ) : (
                <div className="space-y-2" data-testid="customer-quotes-list">
                  {quotes.map(quote => {
                    const linkedEstimate = quote.estimate_id ? estimateById.get(quote.estimate_id) : null;
                    const hasMissingLinkedEstimate = Boolean(quote.estimate_id && !linkedEstimate);
                    return (
                    <div key={quote.id} onClick={() => openRecord('quote', quote)} className="flex cursor-pointer items-center justify-between rounded-sm border border-zinc-200 bg-white p-3 hover:bg-zinc-50" data-testid={`customer-quote-${quote.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-900" data-testid={`customer-quote-number-${quote.id}`}>{quote.quote_number}</p>
                        <p className="text-xs text-zinc-500" data-testid={`customer-quote-summary-${quote.id}`}>{quote.service_category || 'Service'} · {quote.created_at ? new Date(quote.created_at).toLocaleDateString() : ''}</p>
                        {quote.estimate_id && <p className={`mt-1 text-xs font-semibold ${linkedEstimate ? 'text-blue-700' : 'text-amber-800'}`} data-testid={`customer-quote-linked-estimate-${quote.id}`}>{linkedEstimate ? `From estimate: ${linkedEstimate.estimate_number}` : 'Source estimate unavailable'}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold" data-testid={`customer-quote-total-${quote.id}`}>{money(quote.total)}</span>
                        <Badge variant="secondary" className={`text-xs ${quoteStatusColors[quote.status] || ''}`} data-testid={`customer-quote-status-${quote.id}`}>{quote.status}</Badge>
                        {quote.estimate_id && (hasMissingLinkedEstimate ? <Badge variant="secondary" className="border border-amber-200 bg-amber-50 text-amber-800" data-testid={`customer-missing-linked-estimate-${quote.id}`}>Estimate Missing</Badge> : <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={(event) => { event.stopPropagation(); openRecord('estimate', linkedEstimate); }} data-testid={`customer-open-linked-estimate-${quote.id}`}><ExternalLink className="mr-1 h-3 w-3" /> Estimate</Button>)}
                        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" data-testid={`customer-open-quote-button-${quote.id}`}>Open</Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>
    </>
  );
}

export function CustomerJobsTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  return (
    <>
        <TabsContent value="jobs">
          {jobs.length === 0 ? (
            <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No jobs for this customer</p></CardContent></Card>
          ) : (
            <div className="space-y-2" data-testid="customer-jobs-list">
              {jobs.map(job => (
                <div key={job.id} onClick={() => openRecord('job', job)} className="flex cursor-pointer items-center justify-between p-3 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors" data-testid={`customer-open-job-${job.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-1 h-10 rounded-full" style={{ backgroundColor: getDBAColor(job.dba_id) }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{job.title}</p>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" />{job.scheduled_date}</span>
                        {job.scheduled_time_start && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{job.scheduled_time_start}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canUseProofPacket(job) && (
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={(event) => { event.stopPropagation(); createProofPacket(job, false); }} data-testid={`copy-proof-packet-${job.id}`}>
                        <Share2 className="w-3 h-3 mr-1" /> Proof
                      </Button>
                    )}
                    {job.proof_packet_token && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={(event) => { event.stopPropagation(); window.open(proofUrl(job.proof_packet_token), '_blank', 'noopener,noreferrer'); }} data-testid={`open-proof-packet-${job.id}`}>
                        <ExternalLink className="w-3 h-3 mr-1" /> Open
                      </Button>
                    )}
                    {job.estimated_value > 0 && <span className="text-sm font-semibold">{money(job.estimated_value)}</span>}
                    <Badge variant="secondary" className={`text-xs ${statusColors[job.status] || ''}`}>{job.status?.replace('_', ' ')}</Badge>
                    {job.invoice_id && <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" /> Invoiced</Badge>}
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" data-testid={`customer-open-job-button-${job.id}`}>Open</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
    </>
  );
}

export function CustomerPhotosTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  return (
    <>
        <TabsContent value="photos">
          <div className="space-y-4" data-testid="customer-photos-tab">
            <Card className="border border-zinc-200">
              <CardContent className="p-6">
                <PhotoGallery entityType="customer" entityId={customerId} title="Customer Photos" />
              </CardContent>
            </Card>
            <Card className="border border-zinc-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-bold font-heading flex items-center gap-1.5"><Camera className="w-4 h-4" /> Lifecycle Photos</CardTitle></CardHeader>
              <CardContent>
                {photos.length === 0 ? <p className="py-6 text-center text-sm text-zinc-400">No linked lead or job photos yet</p> : (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="customer-lifecycle-photos">
                    {photos.map(photo => (
                      <a key={photo.file_id} href={photoUrl(photo.file_id)} target="_blank" rel="noopener noreferrer" className="group overflow-hidden rounded-md border border-zinc-200 bg-zinc-100" data-testid={`customer-lifecycle-photo-${photo.file_id}`}>
                        <div className="aspect-square overflow-hidden"><img src={photoUrl(photo.file_id)} alt={photo.original_filename} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" /></div>
                        <div className="p-2 text-xs">
                          <Badge variant="secondary" className="mb-1 text-[10px]">{photo.photo_stage || photo.entity_type}</Badge>
                          <p className="truncate text-zinc-600">{photo.original_filename}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
    </>
  );
}

export function CustomerInvoicesTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  return (
    <>
        <TabsContent value="invoices">
          {invoices.length === 0 ? (
            <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No invoices for this customer</p></CardContent></Card>
          ) : (
            <div className="space-y-2" data-testid="customer-invoices-list">
              {invoices.map(invoice => (
                <div key={invoice.id} onClick={() => openRecord('invoice', invoice)} className="flex cursor-pointer items-center justify-between rounded-md border border-zinc-200 bg-white p-3 hover:bg-zinc-50" data-testid={`customer-open-invoice-${invoice.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">{invoice.invoice_number}</p>
                    <p className="text-xs text-zinc-500">{invoice.sent_at ? new Date(invoice.sent_at).toLocaleDateString() : ''} · {invoice.payment_provider || 'manual'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{money(invoice.total)}</span>
                    <Badge variant="secondary" className={`text-xs ${invoiceStatusColors[invoice.status] || ''}`}>{invoice.status}</Badge>
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" data-testid={`customer-open-invoice-button-${invoice.id}`}>Open</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
    </>
  );
}

export function CustomerCommunicationsTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  const smsMessages = messages.filter(message => message.channel === 'sms');
  const statusOf = (message) => message.delivery_status || message.status || 'unknown';
  const delivered = smsMessages.filter(message => statusOf(message) === 'delivered').length;
  const failed = smsMessages.filter(message => ['failed', 'undelivered'].includes(statusOf(message))).length;
  const waiting = smsMessages.filter(message => ['queued', 'accepted', 'sending', 'sent'].includes(statusOf(message))).length;
  return (
    <>
        <TabsContent value="communications">
          <Card className="mb-3 rounded-[1.5rem] border border-blue-100 bg-blue-50 shadow-sm sm:rounded-md" data-testid="customer-sms-delivery-drilldown">
            <CardContent className="p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-base font-black text-blue-950"><MessageSquare className="h-5 w-5 text-blue-700" /> SMS delivery drilldown</h3>
                  <p className="text-sm font-semibold text-blue-700">Customer-level text history with delivery status and provider errors.</p>
                </div>
                <div className="flex flex-wrap gap-2 text-xs font-black">
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700" data-testid="customer-sms-delivered-count">{delivered} delivered</span>
                  <span className="rounded-full bg-sky-100 px-2 py-1 text-sky-700" data-testid="customer-sms-waiting-count">{waiting} waiting</span>
                  <span className="rounded-full bg-red-100 px-2 py-1 text-red-700" data-testid="customer-sms-failed-count">{failed} failed</span>
                </div>
              </div>
            </CardContent>
          </Card>
          {messages.length === 0 ? (
            <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No communications for this customer</p></CardContent></Card>
          ) : (
            <div className="space-y-2" data-testid="customer-communications-list">
              {messages.map(message => (
                <div key={message.id} className="rounded-md border border-zinc-200 bg-white p-3" data-testid={`customer-message-${message.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold capitalize text-zinc-900">{message.channel} · {message.direction}</p>
                    <Badge variant="secondary" className={`text-xs ${['failed', 'undelivered'].includes(statusOf(message)) ? 'bg-red-50 text-red-700' : statusOf(message) === 'delivered' ? 'bg-emerald-50 text-emerald-700' : ''}`} data-testid={`customer-message-status-${message.id}`}>{statusOf(message)}</Badge>
                  </div>
                  {message.subject && <p className="mt-1 text-xs font-semibold text-zinc-700">{message.subject}</p>}
                  <p className="mt-1 text-sm text-zinc-600">{message.text}</p>
                  {['failed', 'undelivered'].includes(statusOf(message)) && <p className="mt-2 flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-bold text-red-700" data-testid={`customer-message-delivery-error-${message.id}`}><AlertTriangle className="h-3 w-3" /> {message.twilio_error_message || message.provider_message || 'Follow up with customer'}</p>}
                  <p className="mt-1 text-xs text-zinc-400">{message.created_at ? new Date(message.created_at).toLocaleString() : ''}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
    </>
  );
}

export function CustomerActivityTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  return (
    <>
        <TabsContent value="activity">
          {activity.length === 0 ? (
            <Card className="border border-zinc-200"><CardContent className="py-8 text-center"><p className="text-zinc-400 text-sm">No activity recorded</p></CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="customer-activity-list">
              {activity.map((act, i) => (
                <div key={act.id || i} className="flex gap-3 p-3 bg-white border border-zinc-200 rounded-md">
                  <div className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-zinc-700">{act.details}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{new Date(act.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
    </>
  );
}

export function CustomerInfoTab({ data, helpers, actions }) {
  const { customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity, timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName, openRecord, createProofPacket, openEditCustomer } = useCustomerTabScope(data, helpers, actions);
  return (
    <>
        <TabsContent value="info">
          <Card className="border border-zinc-200">
            <CardContent className="p-6 space-y-4" data-testid="customer-info-tab">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Email</p><p className="font-medium">{customer.email || '-'}</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Phone</p><p className="font-medium">{formatPhone(customer.phone) || '-'}</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">First Name</p><p className="font-medium" data-testid="customer-info-first-name">{customer.first_name || '-'}</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Last Name</p><p className="font-medium" data-testid="customer-info-last-name">{customer.last_name || '-'}</p></div>
                <div className="col-span-2"><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Address</p><p className="font-medium">{customer.address || '-'}{customer.address && <a href={mapsUrl(customer.address)} target="_blank" rel="noopener noreferrer" className="ml-1 inline-flex align-middle text-zinc-500 hover:text-emerald-700" data-testid="customer-info-map-link" aria-label="Open customer address in Google Maps"><MapPin className="h-3.5 w-3.5" /></a>}</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">City</p><p className="font-medium" data-testid="customer-info-city">{customer.city || '-'}</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">State / ZIP</p><p className="font-medium" data-testid="customer-info-state-zip">{[customer.state, customer.zip_code].filter(Boolean).join(' ') || '-'}</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Source</p><p className="font-medium capitalize">{customer.source || '-'}</p></div>
                <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Created</p><p className="font-medium">{customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}</p></div>
              </div>
              {customer.tags?.length > 0 && <div><p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Tags</p><div className="flex flex-wrap gap-1.5">{customer.tags.map(t => <Badge key={t} variant="outline" className="text-xs"><Tag className="w-3 h-3 mr-1" />{t}</Badge>)}</div></div>}
              <Button type="button" variant="outline" onClick={openEditCustomer} data-testid="customer-info-edit-button">Edit Customer</Button>
            </CardContent>
          </Card>
        </TabsContent>
    </>
  );
}
